import React from 'react';
import { Shield, Lock, Eye, User, FileText, Phone, CheckCircle } from 'lucide-react';
import HimnarioLogo from '../../src/images/himnario-logo.png'; // Ensure this path is correct

// Simple background with minimal elements
const Background = () => (
    <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gray-50" />
    </div>
);

// Clean header component
const Header = () => (
    <header className="text-center mb-16">
        <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6">
            <img
                src={HimnarioLogo}
                alt="Goland.dev Logo"
            />
        </div>

        <h1 className="text-4xl font-light text-gray-900 mb-2">
            Política de Privacidad
        </h1>

        <div className="w-12 h-px bg-gray-300 mx-auto mb-6" />

        <p className="text-sm text-gray-600">
            Última actualización: 18 de Octubre de 2024
        </p>
    </header>
);

// Minimal section component
const Section = ({ icon: Icon, title, children, className = "" }) => (
    <section className={`mb-12 ${className}`}>
        <div className="flex items-center space-x-3 mb-6">
            <Icon className="w-5 h-5 text-gray-700" />
            <h2 className="text-xl font-medium text-gray-900">{title}</h2>
        </div>
        <div className="pl-8">
            {children}
        </div>
    </section>
);

// Simple subsection
const Subsection = ({ title, children }) => (
    <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-800 mb-4">{title}</h3>
        <div className="space-y-4">
            {children}
        </div>
    </div>
);

// Clean list component
const CleanList = ({ items }) => (
    <ul className="space-y-2">
        {items.map((item, index) => (
            <li key={index} className="flex items-start space-x-3">
                <div className="w-1 h-1 bg-gray-400 rounded-full mt-3 flex-shrink-0" />
                <span className="text-gray-700 leading-relaxed text-sm">{item}</span>
            </li>
        ))}
    </ul>
);

// Minimal highlight box
const HighlightBox = ({ children }) => (
    <div className="border-l-2 border-gray-300 pl-4 py-2 bg-gray-50/50">
        <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </div>
);

const PoliticaDePrivacidad = () => {
    return (
        <div className="min-h-screen relative">
            <Background />

            <div className="relative z-10 max-w-3xl mx-auto px-8 py-16">
                <Header />

                {/* Introduction */}
                <div className="mb-16 pb-8 border-b border-gray-200">
                    <p className="text-gray-700 leading-relaxed">
                        Agradecemos su interés y confianza en la aplicación <strong>"Himnario LLDM"</strong>.
                        La privacidad y seguridad de sus datos son de suma importancia para nosotros. Esta Política de Privacidad
                        tiene como objetivo proporcionarle información clara sobre el manejo de datos cuando utiliza nuestra aplicación.
                    </p>
                </div>

                {/* Section 1: Data Collection */}
                <Section icon={User} title="1. Recolección de Información">
                    <Subsection title="1.1. Información Personal">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            La aplicación "Himnario LLDM" no recopila ni almacena directamente ninguna información personal identificable
                            de los usuarios. Sin embargo, nuestra aplicación utiliza servicios de Google que pueden recopilar cierta información:
                        </p>
                        <CleanList items={[
                            "Google Play Billing: Maneja la información necesaria para procesar compras dentro de la aplicación",
                            "Firebase Cloud Messaging: Gestiona los tokens de dispositivo para notificaciones push",
                            "Google AdMob: Administra identificadores de publicidad y preferencias de anuncios",
                            "Firebase Analytics: Recopila datos anónimos de uso"
                        ]} />
                        <HighlightBox>
                            Toda la información personal es manejada directamente por estos servicios de Google bajo sus propias
                            políticas de privacidad y estándares de seguridad.
                        </HighlightBox>
                    </Subsection>

                    <Subsection title="1.2. Información de Uso">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            Los servicios de Google integrados en nuestra aplicación pueden recopilar:
                        </p>
                        <CleanList items={[
                            "Estadísticas anónimas de uso de funciones",
                            "Datos de rendimiento de la aplicación",
                            "Patrones de navegación anónimos",
                            "Información sobre errores",
                            "Métricas de sesión"
                        ]} />
                        <p className="text-gray-600 text-xs mt-4">
                            Esta información es completamente anónima y es procesada por Firebase Analytics y Google Analytics.
                        </p>
                    </Subsection>
                </Section>

                {/* Section 2: Data Usage */}
                <Section icon={Eye} title="2. Uso de la Información">
                    <Subsection title="2.1. Uso por Servicios de Google">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            Los servicios de Google utilizan la información recopilada para:
                        </p>
                        <CleanList items={[
                            "Firebase: Proporcionar análisis anónimos de uso y comportamiento",
                            "AdMob: Gestionar la publicidad relevante",
                            "Play Billing: Procesar transacciones de compra",
                            "Firebase Messaging: Gestionar la entrega de notificaciones"
                        ]} />
                    </Subsection>

                    <Subsection title="2.2. Uso Interno">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            Nosotros únicamente accedemos a datos agregados y anónimos para:
                        </p>
                        <CleanList items={[
                            "Mejorar la funcionalidad de la aplicación",
                            "Optimizar la experiencia del usuario",
                            "Analizar el rendimiento general",
                            "Tomar decisiones sobre futuras actualizaciones"
                        ]} />
                    </Subsection>
                </Section>

                {/* Section 3: Advertising */}
                <Section icon={Eye} title="3. Publicidad">
                    <Subsection title="3.1. Servicios de Publicidad">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            La publicidad es manejada exclusivamente por Google AdMob, que puede:
                        </p>
                        <CleanList items={[
                            "Utilizar el identificador de publicidad de su dispositivo",
                            "Mostrar anuncios basados en intereses",
                            "Recopilar datos de uso para optimizar anuncios"
                        ]} />
                    </Subsection>

                    <Subsection title="3.2. Consentimiento de Publicidad">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            Utilizamos la User Messaging Platform (UMP) de Google para:
                        </p>
                        <CleanList items={[
                            "Gestionar su consentimiento para la publicidad",
                            "Manejar preferencias de privacidad",
                            "Cumplir con regulaciones de privacidad"
                        ]} />
                    </Subsection>
                </Section>

                {/* Section 4: In-App Purchases */}
                <Section icon={Lock} title="4. Compras In-App">
                    <Subsection title="4.1. Google Play Billing">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            Todas las transacciones son procesadas exclusivamente por Google Play Billing:
                        </p>
                        <CleanList items={[
                            "La información de pago es manejada directamente por Google",
                            "No tenemos acceso a sus datos financieros",
                            "Las transacciones siguen los estándares de seguridad de Google Play"
                        ]} />
                    </Subsection>
                </Section>

                {/* Section 5: Security */}
                <Section icon={Shield} title="5. Seguridad">
                    <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                        La seguridad de los datos está garantizada por:
                    </p>
                    <CleanList items={[
                        "Los protocolos de seguridad de Google para sus servicios",
                        "Conexiones seguras con los servicios de Firebase",
                        "Estándares de seguridad de Google Play"
                    ]} />
                </Section>

                {/* Section 6: Rights */}
                <Section icon={CheckCircle} title="6. Sus Derechos y Opciones">
                    <p className="text-gray-700 leading-relaxed mb-4 text-sm">Usted puede:</p>
                    <CleanList items={[
                        "Gestionar sus preferencias de publicidad en la configuración de su dispositivo",
                        "Controlar los permisos de notificaciones",
                        "Administrar sus datos a través de su cuenta de Google",
                        "Desactivar la recopilación de datos de análisis cuando esta opción esté disponible"
                    ]} />
                </Section>

                {/* Section 7: Policy Changes */}
                <Section icon={FileText} title="7. Cambios en la Política">
                    <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                        Los cambios en esta política serán notificados mediante:
                    </p>
                    <CleanList items={[
                        "Actualizaciones de la aplicación",
                        "Mensajes in-app (gestionados por Firebase)",
                        "La interfaz de usuario de la aplicación"
                    ]} />
                </Section>

                {/* Section 8: Contact */}
                <Section icon={Phone} title="8. Contacto">
                    <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                        Para cualquier consulta sobre esta política, puede:
                    </p>
                    <CleanList items={[
                        "Usar la sección de contacto en la aplicación",
                        "Contactarnos a través de Google Play Store",
                        "Utilizar las opciones de contacto disponibles en la app"
                    ]} />
                </Section>

                {/* Section 9: Consent */}
                <Section icon={CheckCircle} title="9. Consentimiento">
                    <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                        Al usar la aplicación, usted acepta:
                    </p>
                    <CleanList items={[
                        "El uso de servicios de Google para análisis y publicidad",
                        "El procesamiento de compras a través de Google Play",
                        "La gestión de notificaciones por Firebase (opcional)"
                    ]} />

                    <HighlightBox>
                        <strong>Nota:</strong> Toda la información personal es manejada exclusivamente por los servicios de Google
                        mencionados anteriormente, siguiendo sus estándares de privacidad y seguridad. La aplicación "Himnario LLDM"
                        no almacena ni procesa directamente ninguna información personal identificable.
                    </HighlightBox>
                </Section>

                {/* Footer */}
                <footer className="text-center mt-16 pt-8 border-t border-gray-200">
                    <p className="text-gray-600 text-xs">
                        Esta política de privacidad está diseñada para ser transparente y fácil de entender.
                        Si tiene alguna pregunta, no dude en contactarnos.
                    </p>
                </footer>
            </div>
        </div>
    );
};

export default PoliticaDePrivacidad;